"use client";

import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import {
    Field,
    FieldContent,
    FieldLabel,
    FieldError,
} from "../../@/components/ui/field";
import { Plus } from "lucide-react";
import { Select, SelectContent, SelectTrigger, SelectItem, SelectValue } from "../../@/components/ui/select";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";

// Validation schema
const addSkillSchema = z.object({
    name: z.string().min(1, "This is required"),
    description: z.string().optional(),
    parentSkillId: z
        .union([z.literal(""), z.coerce.number().int().positive("Parent ID must be a number")])
        .optional(),
});

export default function AddSkillDialog({ skills= [], onAdded }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { getToken } = useAuth();

    const form = useForm({
        resolver: zodResolver(addSkillSchema),
        defaultValues: {
            name: "",
            description: "",
            parentSkillId: "",
        },
    });

    // Get parent skills for dropdown (exclude deeply nested ones to prevent infinite nesting)
    const getParentOptions = () => {
        if (!Array.isArray(skills)) return [];
        return skills.filter(cat => (cat?.level ?? 0) < 2);
    };

    const getPrefix = (level) => {
        if (level === 0) return "";
        if (level === 1) return "— ";
        if (level === 2) return "— — ";
        return "— ".repeat(level);
    };

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            const token = await getToken();
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            };

            const payload = {
                name: data.name.trim(),
                description: data.description?.trim() || "",
                parent_skill_id:
                    data.parentSkillId && data.parentSkillId !== ""
                        ? Number(data.parentSkillId)
                        : null,
            };

            const response = await axios.post(api_endpoint + "/course/skill/create", payload, reqConfig);
            toast.success("Skill created successfully");
            setOpen(false);
            form.reset();

            if (onAdded) {
                onAdded();
            }
        } catch (error) {
            console.error("Error creating skill:", error);
            toast.error("Failed to create skill. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.reset();
        setOpen(false);
    };

    const parentSelectValue = form.watch("parentSkillId")
        ? String(form.watch("parentSkillId"))
        : "none";

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="" className=" w-full md:w-auto">
                    Add Skill
                    <Plus className="h-4 w-4 ml-2" />
                </Button>
            </DialogTrigger>
            <DialogContent className="h-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-(--neutral-900)">
                        Add Skill
                    </DialogTitle>
                    <DialogDescription className="font-body text-(--neutral-700)">
                        Create a new skill with title and description.
                    </DialogDescription>
                </DialogHeader>

                <form
                    id="add-skill-form"
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="space-y-6"
                >
                    {/* Title Field */}
                    <Field>
                        <FieldLabel>Name *</FieldLabel>
                        <FieldContent>
                            <Input
                                id="name"
                                placeholder="Enter skill title"
                                disabled={loading}
                                {...form.register("name")}
                                className={cn(
                                    "font-body",
                                    form.formState.errors.name &&
                                        "border-(--color-destructive) focus-visible:ring-(--color-destructive)"
                                )}
                            />
                            <FieldError
                                errors={
                                    form.formState.errors.name
                                        ? [{ message: form.formState.errors.name.message }]
                                        : []
                                }
                            />
                        </FieldContent>
                    </Field>

                    {/* Description Field */}
                    <Field>
                        <FieldLabel>
                            Description
                            <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                                <Info className="w-3 h-3" />
                                Optional
                            </div>
                        </FieldLabel>
                        <FieldContent>
                            <Textarea
                                id="description"
                                placeholder="Enter skill description"
                                rows={4}
                                className={cn(
                                    "resize-none font-body",
                                    form.formState.errors.description &&
                                        "border-(--color-destructive) focus-visible:ring-(--color-destructive)"
                                )}
                                disabled={loading}
                                {...form.register("description")}
                            />
                            <FieldError
                                errors={
                                    form.formState.errors.description
                                        ? [{ message: form.formState.errors.description.message }]
                                        : []
                                }
                            />
                        </FieldContent>
                    </Field>

                    {/* Parent Skill  */}
                    <Field>
                        <FieldLabel>
                            Parent Skill
                            <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                                <Info className="w-3 h-3" />
                                Optional
                            </div>
                        </FieldLabel>
                        <FieldContent>
                            <Select
                                value={parentSelectValue}
                                onValueChange={(value) =>
                                    form.setValue("parentSkillId", value === "none" ? "" : value)
                                }
                                disabled={loading}
                            >
                                <SelectTrigger
                                    className={cn(
                                        "font-body w-full",
                                        form.formState.errors.parentSkillId &&
                                            "border-(--color-destructive) focus:ring-(--color-destructive)"
                                    )}
                                >
                                    <SelectValue placeholder="Select parent skill" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None (Root Skill)</SelectItem>
                                    {getParentOptions().map((skill) => (
                                        <SelectItem key={skill.id} value={skill.id.toString()}>
                                            {getPrefix(skill.level)}{skill.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FieldError
                                errors={
                                    form.formState.errors.parentSkillId
                                        ? [{ message: form.formState.errors.parentSkillId.message }]
                                        : []
                                }
                            />
                        </FieldContent>
                    </Field>
                </form>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        className="font-body"
                    >
                        Cancel
                    </Button>
                    <Button
                        form="add-skill-form"
                        type="submit"
                        variant=""
                        disabled={loading}
                        className="font-body"
                    >
                        {loading ? <ClipLoader size={20} color="#ffffff" /> : "Create Skill"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}